import React from "react";
import { ButtonVariant, CoButton } from "components/CoButton";
import { usePricingData, SubscriptionPlanIcon } from "features/Pricing";
import { SubscriptionPlan } from "enums/subscription-plans";
import useLabels from "hooks/useLabels";
import { ReactNode } from "react";
import LoadingSubscriptionCard from "../LoadingSubscriptionCard/LoadingSubscriptionCard";
import PricingFeatures from "features/Pricing/components/PricingFeatures";

type Props = {
  children?: ReactNode;
  onUpgradePlan: () => void;
  isLoading?: boolean;
};

/**
 * @component SubscriptionUpgradeFeaturesCard
 * @description lists out the premium features for a user if they would upgrade
 * @example
 * return (
 *   <SubscriptionUpgradeFeaturesCard
 *    onUpgradePlan={clickedUpgradePlan}
 *    isLoading={isPageLoading}
 *  />
 * )
 */
const SubscriptionUpgradeFeaturesCard = ({
  onUpgradePlan,
  isLoading,
}: Props) => {
  const [upgradeFeaturesCardLabels] = useLabels("upgrade-features-card");
  const { pricingData } = usePricingData();
  const premium = pricingData?.premium;

  if (isLoading) {
    return <LoadingSubscriptionCard />;
  }

  return (
    <div className="flex flex-col justify-between gap-4 h-full">
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <SubscriptionPlanIcon plan={SubscriptionPlan.PREMIUM} />
          <h3 className="flex-1 lg-bold">
            {upgradeFeaturesCardLabels["title"]}
          </h3>
        </div>
        <div className="flex-1">
          <PricingFeatures featuresList={premium?.features ?? []} />
        </div>
      </div>
      <CoButton
        className="w-full"
        label={upgradeFeaturesCardLabels["upgrade-to-premium"]}
        onClick={onUpgradePlan}
        size="sm"
        variant={ButtonVariant.Primary}
      />
    </div>
  );
};

export default SubscriptionUpgradeFeaturesCard;
